import { IRootState } from '@/store';
import { ActionTree } from 'vuex';
import { ISecurityState } from './state';
import { NAME } from './';
import { $router } from '@/main';

import { securityService } from '@/services/securityService';
import { AuthServiceCreator } from '@/services/authService';
import {
    BENUTZERINFO,
    PERMISSIONS,
    LOGGEDIN,
    INACTIVITYWARNINGTIME,
    INACTIVITYLOGOUTTIME,
    CHANGEMODALAGBVISIBLE,
    STARTUP_MESSAGE,
    CHANGEMODALSTARTUPMESSAGEVISIBLE
} from './mutations';
import { SHOW, HIDE } from '@/store/modules/loadingSpinner/actions';
import { i18n } from '@/plugins/i18n';
import { startupMessageService } from '@/services/startupMessageService';
import Cookies from 'js-cookie';

export const LOGIN = 'login';
export const LOGOUT = 'logout';
export const INACTIVITYLOGOUT = 'inactivityLogout';
export const GETTOKEN = 'getToken';
export const INITIALIZE = 'initialize';
export const GET_BENUTZERINFO = 'getBenutzerInformationen';
export const GET_PERMISSIONS = 'getPermissions';
export const CHECK_AGB_ACCEPTED = 'checkAgbAccepted';
export const FETCH_STARTUP_MESSAGE = 'fetchStartupMessage';
export const DISMISS_STARTUP_MESSAGE = 'dismissStartupMessage';
export const RESET_INACTIVITYWARNINGTIME = 'resetinactivityWarningTime';
export const RESET_INACTIVITYLOGOUTTIME = 'resetinactivityLogoutTime';
export const UPDATE_INACTIVITYTIMES = 'updateInactivityTimes';
export const RESET_CONTEXT_BY_LOGIN = 'resetContextByLogin';

export enum LoginType {
    default,
    sms,
    totp
}

const DISMISSED_MESSAGES_COOKIE = 'vetdata_startup_messages';
const COOKIE_EXPIRY_DAYS = 365; // Cookie expires after 1 year

export const actions: ActionTree<ISecurityState, IRootState> = {
    async [LOGIN]({ dispatch, commit }: any, type: LoginType) {
        console.debug(NAME + '::' + LOGIN);
        const authService = await AuthServiceCreator.getAuthService();

        dispatch('loadingSpinner/' + SHOW, {}, { root: true });
        let loginFunction: Promise<boolean>;
        if (type == LoginType.sms) {
            loginFunction = authService.loginSms();
        } else if (type == LoginType.totp) {
            loginFunction = authService.loginTotp();
        } else {
            loginFunction = authService.loginTotp();
        }
        //Login führt ein Redirect aus
        return loginFunction.then(async (loggedIn) => {
            commit(LOGGEDIN, loggedIn);
            if (loggedIn) {
                await dispatch(RESET_CONTEXT_BY_LOGIN);
                await dispatch(GET_PERMISSIONS);
                await dispatch(GET_BENUTZERINFO);
                dispatch('loadingSpinner/' + HIDE, {}, { root: true });
            }
        });
    },

    async [LOGOUT]({ commit }: any) {
        console.debug(NAME + '::' + LOGOUT);
        const authService = await AuthServiceCreator.getAuthService();

        authService.logoutRedirect();
        commit(LOGGEDIN, false);
    },

    async [INACTIVITYLOGOUT]({ dispatch, commit }: any) {
        console.debug(NAME + '::' + INACTIVITYLOGOUT);
        const authService = await AuthServiceCreator.getAuthService();

        const showLogoutMessage = async () => {
            dispatch('headerNotification/newInfo', { text: i18n.global.t('inactivity.loggedout') }, { root: true });
            commit(LOGGEDIN, false);
            $router.push({ name: 'Login', params: {} });
        };

        await authService
            .logoutPopup()
            .then(showLogoutMessage)
            .catch(() => {
                authService.logoutRedirect();
            });
    },

    [CHECK_AGB_ACCEPTED]({ dispatch, commit }: any, isAccepted: boolean) {
        console.debug(NAME + '::' + CHECK_AGB_ACCEPTED);
        if (!isAccepted) {
            commit(CHANGEMODALAGBVISIBLE, false);
            dispatch(LOGOUT);
        } else {
            dispatch('loadingSpinner/' + SHOW, {}, { root: true });
            securityService.recordAgbAcceptedDate().then((response) => {
                commit(CHANGEMODALAGBVISIBLE, false);
            });
            dispatch('loadingSpinner/' + HIDE, {}, { root: true });
        }
    },

    async [GETTOKEN]() {
        console.debug(NAME + '::' + GETTOKEN);
        const authService = await AuthServiceCreator.getAuthService();

        authService.getAuthorizationHeader();
    },

    async [INITIALIZE]({ commit, dispatch }: any) {
        console.debug(NAME + '::' + INITIALIZE);
        const authService = await AuthServiceCreator.getAuthService();
        await authService.isLoggedIn().then(async (success) => {
            if (success) {
                await dispatch(RESET_CONTEXT_BY_LOGIN);
                await dispatch(GET_PERMISSIONS);
                await dispatch(GET_BENUTZERINFO);
            }
            commit(LOGGEDIN, success);
        });
    },
    async [GET_BENUTZERINFO]({ commit, state }: any) {
        console.debug(NAME + '::' + GET_BENUTZERINFO);
        await securityService.getBenutzerInfo().then((response) => {
            if (state.loggedIn) {
                if (response.rollen.includes('UserMain') || response.rollen.includes('UserBuha')) {
                    $router.push({ name: 'Home', params: {} });
                } else if (response.rollen.includes('UserStaff') || response.rollen.includes('UserView')) {
                    $router.push({ name: 'Lieferantenrechnungen', params: {} });
                } else if (response.rollen.includes('UserUpload')) {
                    $router.push({ name: 'Dokumentarchiv', params: {} });
                } else if (response.rollen.includes('SysAdmin')) {
                    $router.push({ name: 'TakeTeilnehmer', params: {} });
                } else if (response.rollen.includes('PraxisgruppeMain') || response.rollen.includes('PraxisgruppeUser')) {
                    $router.push({ name: 'PraxisgruppeRechnungen', params: {} });
                } else if (response.rollen.includes('TreuhandMain') || response.rollen.includes('TreuhandUser')) {
                    $router.push({ name: 'TreuhandTeilnehmerListe', params: {} });
                } else if (response.rollen.includes('LieferantMain') || response.rollen.includes('LieferantUser')) {
                    $router.push({ name: 'LieferantRechnungen', params: {} });
                } else {
                    $router.push({ name: 'Dokumentarchiv', params: {} });
                }
            }
            if (
                !response.istAgbAngenommen &&
                !response.rollen.includes('SysAdmin') &&
                !state.permissions?.includes('navigation.taketeilnehmer'.toLowerCase())
            ) {
                commit(CHANGEMODALAGBVISIBLE, true);
            } else {
                commit(CHANGEMODALAGBVISIBLE, false);
            }
            commit(BENUTZERINFO, response);
        });
    },
    async [GET_PERMISSIONS]({ commit }: any) {
        console.debug(NAME + '::' + GET_PERMISSIONS);
        await securityService.getAll().then((response) => {
            commit(PERMISSIONS, response);
        });
    },
    async [FETCH_STARTUP_MESSAGE]({ commit }: any) {
        console.debug(NAME + '::' + FETCH_STARTUP_MESSAGE);
        try {
            const message = await startupMessageService.getStartupMessage();

            if (message && message.messageDe && message.messageDe.trim() !== '' && message.messageFr && message.messageFr.trim() !== '') {
                // Check validUntil
                if (message.validUntil) {
                    const validUntil = new Date(message.validUntil);
                    if (new Date() > validUntil) {
                        return;
                    }
                }

                // Generate hash from message content
                const messageHash = await startupMessageService.generateMessageHash(message.messageDe, message.messageFr);

                // Check if this message hash has been dismissed
                const dismissedMessagesCookie = Cookies.get(DISMISSED_MESSAGES_COOKIE);
                if (dismissedMessagesCookie) {
                    try {
                        const dismissedHashes: string[] = JSON.parse(dismissedMessagesCookie);
                        if (Array.isArray(dismissedHashes) && dismissedHashes.includes(messageHash)) {
                            console.debug('Startup message already dismissed (hash: ' + messageHash + ')');
                            return;
                        }
                    } catch (e) {
                        console.error('Failed to parse dismissed messages cookie', e);
                    }
                }

                commit(STARTUP_MESSAGE, message);
                commit(CHANGEMODALSTARTUPMESSAGEVISIBLE, true);
            }
        } catch (error) {
            console.error('Failed to fetch startup message', error);
        }
    },
    async [DISMISS_STARTUP_MESSAGE]({ commit, state }: any, dontShowAgain: boolean) {
        console.debug(NAME + '::' + DISMISS_STARTUP_MESSAGE, { dontShowAgain });

        if (dontShowAgain && state.startupMessage) {
            // Generate hash from current message
            const messageHash = await startupMessageService.generateMessageHash(state.startupMessage.messageDe, state.startupMessage.messageFr);

            // Get existing dismissed hashes from cookie
            const dismissedMessagesCookie = Cookies.get(STARTUP_MESSAGE);
            let dismissedHashes: string[] = [];

            if (dismissedMessagesCookie) {
                try {
                    dismissedHashes = JSON.parse(dismissedMessagesCookie);
                    if (!Array.isArray(dismissedHashes)) {
                        dismissedHashes = [];
                    }
                } catch (e) {
                    console.error('Failed to parse dismissed messages cookie', e);
                    dismissedHashes = [];
                }
            }

            // Add new hash if not already present
            if (!dismissedHashes.includes(messageHash)) {
                dismissedHashes.push(messageHash);

                // Store back in cookie (expires after 1 year)
                Cookies.set(DISMISSED_MESSAGES_COOKIE, JSON.stringify(dismissedHashes), {
                    expires: COOKIE_EXPIRY_DAYS
                });

                console.debug('Startup message dismissed and saved to cookie (hash: ' + messageHash + ')');
            }
        }

        // Close the dialog
        commit(CHANGEMODALSTARTUPMESSAGEVISIBLE, false);
    },
    [RESET_INACTIVITYWARNINGTIME]({ commit, state }) {
        console.debug(NAME + '::' + RESET_INACTIVITYWARNINGTIME);
        commit(INACTIVITYWARNINGTIME, Date.now() + state.inactivityWarningTimeout * 60 * 1000);
    },

    [RESET_INACTIVITYLOGOUTTIME]({ commit, state }) {
        console.debug(NAME + '::' + RESET_INACTIVITYLOGOUTTIME);
        commit(INACTIVITYLOGOUTTIME, Date.now() + state.inactivityLogoutTimeout * 60 * 1000);
    },

    async [RESET_CONTEXT_BY_LOGIN]({ dispatch }: any) {
        console.debug(NAME + '::' + RESET_CONTEXT_BY_LOGIN);
        const response = await securityService.getBenutzerInfo();
        if (response.isKontextSet) {
            console.log('Kontext wird resetted');
            await securityService.resetContext();
        }
    },

    /**Inaktivität aus localstorage lesen, könnte von anderem Tab aktualisiert worden sein */
    [UPDATE_INACTIVITYTIMES]({ state, commit }) {
        const warningTime = parseInt(localStorage.getItem(INACTIVITYWARNINGTIME), 10);
        const logouttime = parseInt(localStorage.getItem(INACTIVITYLOGOUTTIME), 10);

        const updateTimesInStore = state.inactivityWarningTime != warningTime || state.inactivitylogoutTime != logouttime;

        if (updateTimesInStore) {
            commit(INACTIVITYWARNINGTIME, warningTime.toString());
            commit(INACTIVITYLOGOUTTIME, logouttime.toString());
        }
    }
};
